import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { publicApi } from "@/lib/api";
import PublicLayout from "@/components/public/PublicLayout";
import PreviewBanner from "@/components/public/PreviewBanner";
import PublicFileUpload from "@/components/public/PublicFileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, ShieldCheck } from "lucide-react";
import { usePageIntro, PAGE_INTROS } from "@/lib/cms";

const STEPS = ["You & Intent", "The Watch", "Disclosures & Price", "Photos & Agreements"];
const RECEIPT = [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }, { value: "unsure", label: "Not sure" }];
const receiptToBool = (v) => (v === "yes" ? true : v === "no" ? false : null);

const EMPTY = {
  full_name: "", email: "", phone: "", location: "", intent: "selling",
  brand: "", model: "", reference_number: "", serial_private: "", year: "", condition: "",
  box_papers: "", service_history: "", purchase_history: "",
  original_receipt: "unsure", aftermarket_parts: "", custom_work: "", known_issues: "",
  asking_price: "", payout_timeline: "",
  watch_photos: [], paperwork_photos: [],
  ownership_statement: false, agree_inspection: false, agree_truthful: false,
};

function Field({ label, children, hint }) {
  return (<div className="space-y-2"><Label className="text-[var(--text-2)] text-sm">{label}</Label>{children}{hint && <p className="text-xs text-[var(--muted)]">{hint}</p>}</div>);
}
const inputCls = "bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]";

export default function SellConsign() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [intro, preview] = usePageIntro(PAGE_INTROS.sell.key, PAGE_INTROS.sell.defaults);
  const agreementsOk = form.ownership_statement && form.agree_inspection && form.agree_truthful;

  const next = () => {
    if (step === 0 && (!form.full_name || !form.email)) { toast.error("Name and email are required"); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!agreementsOk) { toast.error("Please accept all three agreements to submit"); return; }
    setBusy(true);
    try {
      const strip = (arr) => arr.map(({ id, url, filename, content_type }) => ({ id, url, filename, content_type, visibility: "private" }));
      const payload = {
        ...form,
        asking_price: form.asking_price === "" ? null : Number(form.asking_price),
        original_receipt: receiptToBool(form.original_receipt),
        watch_photos: strip(form.watch_photos),
        paperwork_photos: strip(form.paperwork_photos),
      };
      await publicApi.post("/public/seller-intake", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <PublicLayout>
        <section className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="sell-confirmation">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full border border-[#c9a86a] text-[#c9a86a] mb-6"><Check size={28} /></div>
          <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3">INTAKE RECEIVED</div>
          <h1 className="text-3xl md:text-4xl font-semibold text-[var(--text)]">Thank you. We'll be in touch.</h1>
          <p className="text-[var(--text-2)] mt-6 leading-relaxed">
            Our team will review your submission and respond with next steps. Every piece must pass
            independent inspection and authentication before purchase or consignment is finalized.
          </p>
          <div className="mt-10"><Link to="/"><Button className="bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none px-8 py-6">Back to Home</Button></Link></div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="max-w-3xl mx-auto px-5 py-14">
        <div className="text-center mb-8">
          <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3">{intro.eyebrow}</div>
          <h1 className="text-3xl md:text-4xl font-semibold text-[var(--text)]">{intro.title}</h1>
          <p className="text-[var(--text-2)] mt-3">{intro.subtitle}</p>
        </div>

        <div className="flex items-center justify-between mb-8" data-testid="sell-progress">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1 flex items-center">
              <div className="flex flex-col items-center">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs border ${i <= step ? "bg-[#c9a86a] text-[var(--on-gold)] border-[#c9a86a]" : "bg-transparent text-[var(--muted)] border-[var(--border-2)]"}`}>{i < step ? <Check size={14} /> : i + 1}</div>
                <span className={`text-[10px] mt-1.5 hidden sm:block ${i <= step ? "text-[#c9a86a]" : "text-[var(--muted)]"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-[#c9a86a]" : "bg-[var(--border-2)]"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-[var(--panel-2)] border border-[var(--border-2)] rounded-xl p-6 md:p-8 space-y-5" data-testid="sell-form">
          {step === 0 && (
            <>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name *"><Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} data-testid="sell-name" /></Field>
                <Field label="Email *"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="sell-email" /></Field>
                <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></Field>
                <Field label="Location"><Input value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} /></Field>
              </div>
              <Field label="Are you selling or consigning?">
                <Select value={form.intent} onValueChange={(v) => set("intent", v)}>
                  <SelectTrigger className={inputCls} data-testid="sell-intent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="selling">Selling outright</SelectItem>
                    <SelectItem value="consigning">Consigning</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </>
          )}

          {step === 1 && (
            <>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Brand"><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} className={inputCls} data-testid="sell-brand" /></Field>
                <Field label="Model"><Input value={form.model} onChange={(e) => set("model", e.target.value)} className={inputCls} /></Field>
                <Field label="Reference Number"><Input value={form.reference_number} onChange={(e) => set("reference_number", e.target.value)} className={inputCls} /></Field>
                <Field label="Serial / Private Identifier" hint="Optional. Kept strictly private and never shown publicly."><Input value={form.serial_private} onChange={(e) => set("serial_private", e.target.value)} className={inputCls} /></Field>
                <Field label="Year"><Input value={form.year} onChange={(e) => set("year", e.target.value)} className={inputCls} /></Field>
                <Field label="Condition"><Input value={form.condition} onChange={(e) => set("condition", e.target.value)} className={inputCls} /></Field>
                <Field label="Box & Papers"><Input value={form.box_papers} onChange={(e) => set("box_papers", e.target.value)} className={inputCls} /></Field>
                <Field label="Service History"><Input value={form.service_history} onChange={(e) => set("service_history", e.target.value)} className={inputCls} /></Field>
              </div>
              <Field label="Purchase History"><Textarea rows={2} value={form.purchase_history} onChange={(e) => set("purchase_history", e.target.value)} className={inputCls} placeholder="Where/when purchased, original owner, etc." /></Field>
            </>
          )}

          {step === 2 && (
            <>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Original Receipt?"><Select value={form.original_receipt} onValueChange={(v) => set("original_receipt", v)}><SelectTrigger className={inputCls}><SelectValue /></SelectTrigger><SelectContent>{RECEIPT.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></Field>
                <Field label="Asking Price (USD)"><Input type="number" value={form.asking_price} onChange={(e) => set("asking_price", e.target.value)} className={inputCls} data-testid="sell-price" /></Field>
                <Field label="Aftermarket Parts?" hint="List any non-factory parts."><Input value={form.aftermarket_parts} onChange={(e) => set("aftermarket_parts", e.target.value)} className={inputCls} placeholder="e.g. none / aftermarket bezel" /></Field>
                <Field label="Diamonds / Gold / Custom Work?"><Input value={form.custom_work} onChange={(e) => set("custom_work", e.target.value)} className={inputCls} placeholder="e.g. none / factory diamond dial" /></Field>
                <Field label="Desired Payout Timeline"><Input value={form.payout_timeline} onChange={(e) => set("payout_timeline", e.target.value)} className={inputCls} placeholder="e.g. ASAP, flexible" /></Field>
              </div>
              <Field label="Known Issues" hint="Full disclosure protects the transaction for everyone."><Textarea rows={2} value={form.known_issues} onChange={(e) => set("known_issues", e.target.value)} className={inputCls} /></Field>
            </>
          )}

          {step === 3 && (
            <>
              <Field label="Watch Photos" hint="Front, back, side, caseback, bracelet, clasp, dial, and movement if possible.">
                <PublicFileUpload files={form.watch_photos} onChange={(fs) => set("watch_photos", fs)} category="seller_watch" testId="sell-watch-photos" />
              </Field>
              <Field label="Paperwork Photos" hint="Warranty card, receipts, service papers (images or PDF).">
                <PublicFileUpload files={form.paperwork_photos} onChange={(fs) => set("paperwork_photos", fs)} accept="image/*,application/pdf" category="seller_paperwork" testId="sell-paper-photos" />
              </Field>

              <div className="border border-[var(--border-2)] rounded-lg p-4 space-y-3 bg-[var(--panel)]">
                <div className="flex items-center gap-2 text-[#c9a86a] text-sm"><ShieldCheck size={16} /> Required Agreements</div>
                <label className="flex items-start gap-3 cursor-pointer"><Checkbox checked={form.ownership_statement} onCheckedChange={(v) => set("ownership_statement", !!v)} data-testid="agree-ownership" className="mt-0.5" /><span className="text-sm text-[var(--text-2)]">I confirm I am the rightful owner with full authority to sell or consign this watch.</span></label>
                <label className="flex items-start gap-3 cursor-pointer"><Checkbox checked={form.agree_inspection} onCheckedChange={(v) => set("agree_inspection", !!v)} data-testid="agree-inspection" className="mt-0.5" /><span className="text-sm text-[var(--text-2)]">I understand the watch must pass independent inspection and authentication before any purchase or consignment is finalized.</span></label>
                <label className="flex items-start gap-3 cursor-pointer"><Checkbox checked={form.agree_truthful} onCheckedChange={(v) => set("agree_truthful", !!v)} data-testid="agree-truthful" className="mt-0.5" /><span className="text-sm text-[var(--text-2)]">I understand that false or incomplete information may cancel the transaction.</span></label>
              </div>
            </>
          )}

          <div className="flex justify-between pt-4 border-t border-[var(--border-2)]">
            <Button type="button" variant="outline" onClick={back} disabled={step === 0} className="border-[var(--border-2)] bg-transparent text-[var(--text-2)] rounded-none disabled:opacity-30"><ArrowLeft size={16} className="mr-2" />Back</Button>
            {step < STEPS.length - 1 ? (
              <Button type="button" onClick={next} data-testid="sell-next" className="bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none">Continue<ArrowRight size={16} className="ml-2" /></Button>
            ) : (
              <Button type="button" onClick={submit} disabled={busy || !agreementsOk} data-testid="sell-submit" className="bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none disabled:opacity-40">{busy ? "Submitting…" : "Submit Intake"}</Button>
            )}
          </div>
        </div>
      </section>
      {preview && <PreviewBanner testId="sell-preview-banner" />}
    </PublicLayout>
  );
}
